import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { StaticCard } from '../components/BorderCard';
import { Shirt, Layers, Sparkles, CheckCircle, ShoppingBag } from 'lucide-react';

const stylingPrinciples = [
  {
    id: 1,
    title: 'Role Mindset',
    icon: Sparkles,
    description: 'You are a Style Curator. Understand the customer\'s vibe, occasion, and comfort before suggesting anything.'
  },
  {
    id: 2,
    title: 'Build the Look',
    icon: Layers,
    description: 'Start with one key piece → add complementary products (shirt + denim + layer).'
  },
  {
    id: 3,
    title: 'Offer Options',
    icon: Shirt,
    description: 'Always present 2–3 styling combinations (casual / smart casual / elevated).'
  },
  {
    id: 4,
    title: 'Keep It Clean',
    icon: CheckCircle,
    description: 'Focus on minimal, well-coordinated, premium looks.'
  },
  {
    id: 5,
    title: 'Sell the Outfit, Not the Product',
    icon: ShoppingBag,
    description: 'Ensure the customer walks out with a complete look.'
  }
];

const stylingOptions = [
  {
    title: 'Casual',
    items: ['T-shirt', 'Denim', 'Sneakers'],
    vibe: 'Relaxed everyday'
  },
  {
    title: 'Smart Casual',
    items: ['Polo', 'Chinos', 'Loafers'],
    vibe: 'Effortless polish'
  },
  {
    title: 'Elevated',
    items: ['Shirt', 'Trousers', 'Layer'],
    vibe: 'Refined sophistication'
  }
];

const PrincipleCard = ({ principle, index }) => {
  const Icon = principle.icon;
  
  return (
    <motion.div
      className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-6 cursor-default"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#F8F6F2] border border-[rgba(26,26,26,0.1)] flex items-center justify-center flex-shrink-0">
          <Icon size={18} className="text-[#1A1A1A]" />
        </div>
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1A1A1A] mb-2">
            {principle.title}
          </h4>
          <p className="text-xs text-[#1A1A1A]/70 leading-relaxed">
            {principle.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const OutfitCard = ({ option, index }) => {
  return (
    <motion.div
      className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-6 text-center cursor-default"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      viewport={{ once: true }}
    >
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F8F6F2] border border-[rgba(26,26,26,0.1)] flex items-center justify-center">
        <Shirt size={24} className="text-[#1A1A1A]" />
      </div>
      <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-[#1A1A1A] mb-2">
        {option.title}
      </h4>
      <p className="text-[10px] uppercase tracking-wider text-[#D32F2F] mb-4">
        {option.vibe}
      </p>
      <div className="space-y-2">
        {option.items.map((item, idx) => (
          <div 
            key={idx} 
            className="text-xs text-[#1A1A1A]/70 py-1 border-b border-[rgba(26,26,26,0.05)] last:border-b-0"
          >
            {item}
          </div>
        ))}
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-4">
        For reference only
      </div>
    </motion.div>
  );
};

const StylingPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-[#1A1A1A] mb-6">
            Styling
          </h1>
          
          {/* Training Reference Label */}
          <p className="text-xs uppercase tracking-[0.15em] text-[#D32F2F] font-semibold mb-6">
            Training Reference Link
          </p>
          
          {/* Embedded Flipbook */}
          <motion.div
            className="w-full rounded-xl overflow-hidden shadow-sm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              boxShadow: '0 2px 12px rgba(26, 26, 26, 0.06)'
            }}
          >
            <iframe 
              allowFullScreen
              allow="clipboard-write" 
              scrolling="no" 
              className="fp-iframe" 
              src="https://heyzine.com/flip-book/ecb1d2a220.html" 
              style={{
                border: 'none',
                width: '100%',
                height: '520px',
                borderRadius: '12px'
              }}
              title="Styling Training Flipbook"
            />
          </motion.div>
        </motion.div>

        {/* Section Title */}
        <FadeIn delay={0.2}>
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-light text-[#1A1A1A] italic">
              Style Like a Curator, Not a Seller
            </h2>
          </div>
        </FadeIn>

        {/* Styling Principles */}
        <FadeIn delay={0.3}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            The Curator's Approach
          </h3>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {stylingPrinciples.map((principle, index) => (
            <PrincipleCard key={principle.id} principle={principle} index={index} />
          ))}
        </div>

        {/* Styling Flow */}
        <FadeIn delay={0.5}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-4 text-sm text-[#1A1A1A]/60">
              <span className="px-4 py-2 bg-white rounded border border-[rgba(26,26,26,0.08)] cursor-default">Pick</span>
              <span className="text-[#D32F2F]">→</span>
              <span className="px-4 py-2 bg-white rounded border border-[rgba(26,26,26,0.08)] cursor-default">Pair</span>
              <span className="text-[#D32F2F]">→</span>
              <span className="px-4 py-2 bg-white rounded border border-[rgba(26,26,26,0.08)] cursor-default">Elevate</span>
            </div>
          </div>
        </FadeIn>

        {/* Outfit Options */}
        <FadeIn delay={0.6}>
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-center text-[#1A1A1A]/60 mb-8">
            Styling Combinations
          </h3>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {stylingOptions.map((option, index) => (
            <OutfitCard key={option.title} option={option} index={index} />
          ))}
        </div>

        {/* Callout Quote */}
        <FadeIn delay={0.7}>
          <StaticCard className="p-10 text-center rounded-lg">
            <p className="text-xl md:text-2xl font-light text-[#1A1A1A] italic leading-relaxed">
              "Great styling doesn't sell clothes. It sells confidence."
            </p>
          </StaticCard>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default StylingPage;
